import { useCallback, useId, useState } from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import * as documentService from "../../services/documentService";
import type { Document, DocumentCategory } from "../../types/document";
import { Dropdown } from "../ui/form";
import styles from "./DocumentUploader.module.css";

interface DocumentUploaderProps {
	onUploadComplete: (document: Document) => void;
	isUploading: boolean;
	setIsUploading: (uploading: boolean) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ALLOWED_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"text/plain",
];

export function DocumentUploader({
	onUploadComplete,
	isUploading,
	setIsUploading,
}: DocumentUploaderProps) {
	const { t } = useTranslation("documents");
	const { logEvent } = useAnalytics();
	const descriptionId = useId();
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [description, setDescription] = useState("");
	const [category, setCategory] = useState<DocumentCategory>("other");
	const [uploadProgress, setUploadProgress] = useState(0);

	// Auto-detect category helper function
	const autoDetectCategory = useCallback(
		(fileName: string, _mimeType: string): DocumentCategory => {
			const lowerName = fileName.toLowerCase();

			if (
				lowerName.includes("lab") ||
				lowerName.includes("result") ||
				lowerName.includes("test")
			) {
				return "lab_results";
			}
			if (
				lowerName.includes("prescription") ||
				lowerName.includes("rx") ||
				lowerName.includes("medication")
			) {
				return "prescriptions";
			}
			if (
				lowerName.includes("imaging") ||
				lowerName.includes("ultrasound") ||
				lowerName.includes("xray") ||
				lowerName.includes("mri") ||
				lowerName.includes("ct")
			) {
				return "imaging";
			}
			if (
				lowerName.includes("medical") ||
				lowerName.includes("record") ||
				lowerName.includes("history")
			) {
				return "medical_records";
			}
			if (
				lowerName.includes("insurance") ||
				lowerName.includes("coverage") ||
				lowerName.includes("eob")
			) {
				return "insurance";
			}

			return "other";
		},
		[],
	);

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (acceptedFiles.length === 0) return;

			const file = acceptedFiles[0];

			// Validate file size
			if (file.size > MAX_FILE_SIZE) {
				toast.error(t("upload.errors.sizeTooLarge"));
				return;
			}

			// Validate file type
			if (!ALLOWED_MIME_TYPES.includes(file.type)) {
				toast.error(t("upload.errors.invalidType"));
				return;
			}

			setSelectedFile(file);

			// Auto-detect category based on file name/type
			const detectedCategory = autoDetectCategory(file.name, file.type);
			setCategory(detectedCategory);

			logEvent("document_file_selected", undefined, {
				file_type: file.type,
				file_size_kb: Math.round(file.size / 1024),
				auto_category: detectedCategory,
			});
		},
		[t, autoDetectCategory, logEvent],
	);

	// react-dropzone rejects ALL files when more than maxFiles (1) are dropped, so
	// a multi-file selection lands here with `acceptedFiles` empty — previously it
	// fell through onDrop's empty-accepted guard and did nothing. Surface a clear
	// message per rejection cause instead of silently no-op'ing.
	const onDropRejected = useCallback(
		(rejections: FileRejection[]) => {
			const codes = new Set(
				rejections.flatMap((r) => r.errors.map((e) => e.code)),
			);
			if (codes.has("too-many-files")) {
				toast.error(t("upload.errors.tooManyFiles"));
			} else if (codes.has("file-too-large")) {
				toast.error(t("upload.errors.sizeTooLarge"));
			} else if (codes.has("file-invalid-type")) {
				toast.error(t("upload.errors.invalidType"));
			} else {
				toast.error(t("upload.errors.uploadFailed"));
			}
		},
		[t],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		onDropRejected,
		accept: {
			"image/jpeg": [".jpg", ".jpeg"],
			"image/png": [".png"],
			"application/pdf": [".pdf"],
			"application/msword": [".doc"],
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
				[".docx"],
			"text/plain": [".txt"],
		},
		maxFiles: 1,
		maxSize: MAX_FILE_SIZE,
		disabled: isUploading,
	});

	const handleUpload = async () => {
		if (!selectedFile) return;

		setIsUploading(true);
		setUploadProgress(0);

		try {
			const formData = new FormData();
			formData.append("file", selectedFile);
			formData.append("description", description);
			formData.append("category", category);

			const response = await documentService.uploadDocument(
				formData,
				(progress) => {
					setUploadProgress(progress);
				},
			);

			onUploadComplete(response.document);

			// Reset form
			setSelectedFile(null);
			setDescription("");
			setCategory("other");
			setUploadProgress(0);
		} catch (error) {
			console.error("Upload error:", error);
			toast.error(t("upload.errors.uploadFailed"));
		} finally {
			setIsUploading(false);
		}
	};

	const handleCancel = () => {
		setSelectedFile(null);
		setDescription("");
		setCategory("other");
		setUploadProgress(0);
	};

	const categories: Array<{ value: DocumentCategory; label: string }> = [
		{ value: "lab_results", label: t("categories.lab_results") },
		{ value: "prescriptions", label: t("categories.prescriptions") },
		{ value: "imaging", label: t("categories.imaging") },
		{
			value: "medical_records",
			label: t("categories.medical_records"),
		},
		{ value: "insurance", label: t("categories.insurance") },
		{ value: "other", label: t("categories.other") },
	];

	return (
		<div className={styles.uploader}>
			{!selectedFile ? (
				<div
					{...getRootProps()}
					className={`${styles.dropzone} ${isDragActive ? styles.active : ""} ${isUploading ? styles.disabled : ""}`}
				>
					<input {...getInputProps()} />
					<i className="pi pi-cloud-upload" style={{ fontSize: "3rem" }} />
					<h3>{t("upload.dropzone.title")}</h3>
					<p>{t("upload.dropzone.description")}</p>
					<button type="button" className={styles.browseButton}>
						{t("upload.dropzone.browse")}
					</button>
					<p className={styles.hint}>{t("upload.dropzone.formats")}</p>
				</div>
			) : (
				<div className={styles.uploadForm}>
					<div className={styles.filePreview}>
						<i className="pi pi-file" style={{ fontSize: "2rem" }} />
						<div className={styles.fileInfo}>
							<p className={styles.fileName}>{selectedFile.name}</p>
							<p className={styles.fileSize}>
								{(selectedFile.size / 1024 / 1024).toFixed(2)} MB
							</p>
						</div>
						{!isUploading && (
							<button
								type="button"
								onClick={handleCancel}
								className={styles.removeButton}
								aria-label={t("upload.cancel")}
							>
								<i className="pi pi-times" />
							</button>
						)}
					</div>

					{isUploading && uploadProgress > 0 && (
						<div className={styles.progressBar}>
							<div
								className={styles.progress}
								style={{ width: `${uploadProgress}%` }}
							/>
						</div>
					)}

					{!isUploading && (
						<>
							<div className={styles.formGroup}>
								<span className={styles.label}>{t("upload.category")}</span>
								<Dropdown
									options={categories}
									value={category}
									onChange={(value) => setCategory(value as DocumentCategory)}
									placeholder={t("categories.other")}
									className={styles.categoryDropdown}
								/>
							</div>

							<div className={styles.formGroup}>
								<label htmlFor={descriptionId} className={styles.label}>
									{t("upload.description")}{" "}
									<span className={styles.optional}>
										({t("upload.optional")})
									</span>
								</label>
								<textarea
									id={descriptionId}
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder={t("upload.descriptionPlaceholder")}
									className={styles.textarea}
									rows={3}
								/>
							</div>

							<div className={styles.actions}>
								<button
									type="button"
									onClick={handleCancel}
									className={styles.cancelButton}
								>
									{t("upload.cancel")}
								</button>
								<button
									type="button"
									onClick={handleUpload}
									className={styles.uploadButton}
								>
									<i className="pi pi-upload" />
									{t("upload.submit")}
								</button>
							</div>
						</>
					)}

					{isUploading && (
						<p className={styles.uploadingText}>{t("upload.uploading")}</p>
					)}
				</div>
			)}
		</div>
	);
}
